import styles from './ArticlePurchasePriceCell.module.scss';

import {
  Paper,
  Popper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSinglePriceWithContext } from '@work4all/data';

import { Article } from '@work4all/models/lib/Classes/Article.entity';

import { formatNumberAsCurrency } from '@work4all/utils';
import {
  DateTimeCustom,
  formatDateString,
} from '@work4all/utils/lib/date-utils/formatDateString';

import { createFooterCell } from './create-footer-cell';

export type IArticlePurchasePriceCellProps = {
  row?: {
    original: Article;
  };
  column?: {
    id: string;
    cellParams: {
      source?: 'table' | 'positions';
      onSelected?: (article: Article, purchasePriceId: number) => void;
    };
  };
};

export const ArticlePurchasePriceCell: React.FC<
  IArticlePurchasePriceCellProps
> = (props) => {
  const { t } = useTranslation();

  const { row } = props;

  const standardSupplierId =
    row?.original.standardSupplierId ?? row?.original.defaultSupplier?.id;
  const purchasePriceList = row?.original.articlePrices?.purchasePriceList;

  const standardPurchasePrice = purchasePriceList?.find(
    (x) => x.supplier?.id === standardSupplierId
  );
  const standardPurchasePriceValue = standardPurchasePrice?.purchasePrice;
  const defaultPriceValue =
    standardPurchasePriceValue ?? purchasePriceList?.[0]?.purchasePrice ?? null;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'supplier-tooltip' : undefined;

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 200); // delay to allow mouse to move into 'tooltip'
  };

  const options = useSinglePriceWithContext();

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      justifyContent="flex-end"
      gap={0.5}
    >
      <Typography variant="body2">
        {defaultPriceValue
          ? formatNumberAsCurrency(defaultPriceValue, options)
          : ''}
      </Typography>
      {purchasePriceList?.length > 1 && (
        <Typography
          variant="body2"
          className={styles.secondaryInfo}
          aria-describedby={id}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {`(+${purchasePriceList.length - 1})`}
        </Typography>
      )}

      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        onMouseEnter={() => {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
        }}
        onMouseLeave={handleMouseLeave}
        className={styles.popper}
      >
        <Paper elevation={3} className={styles.paper}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    className={clsx(styles.tableCell, styles.tableHeadCell)}
                  >
                    {t('COMMON.BUSINESS_PARTNER_LIEFERANT')}
                  </TableCell>
                  <TableCell
                    className={clsx(styles.tableCell, styles.tableHeadCell)}
                  >
                    {t('MASK.VALID_FROM')}
                  </TableCell>
                  <TableCell
                    align="right"
                    className={clsx(styles.tableCell, styles.tableHeadCell)}
                  >
                    {t(
                      'MASK.ARTICLE.PURCHASE_PRICE.FIELDS.AMOUNT_PER_UNIT_SHORT'
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    className={clsx(styles.tableCell, styles.tableHeadCell)}
                  >
                    {t('COMMON.ERP.PURCHASE_PRICE')}
                  </TableCell>
                  <TableCell
                    className={clsx(styles.tableCell, styles.tableHeadCell)}
                  >
                    {t('COMMON.CURRENCY')}
                  </TableCell>
                  {/* ToDo: Available to do when https://work4all.atlassian.net/browse/WW-6413 is ready */}
                  {/* {source === 'positions' && (
                    <TableCell className={styles.tableCell} />
                  )} */}
                </TableRow>
              </TableHead>
              <TableBody>
                {purchasePriceList?.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className={styles.tableCell}>
                      {row.supplier?.name}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {formatDateString(
                        row.validFromDate,
                        DateTimeCustom.DATE_SHORT
                      )}
                    </TableCell>
                    <TableCell align="right" className={styles.tableCell}>
                      {row.amountPerUnit}
                    </TableCell>
                    <TableCell align="right" className={styles.tableCell}>
                      {formatNumberAsCurrency(row.purchasePrice, options)}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {row.currency?.name}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Popper>
    </Stack>
  );
};

export const ArticlePurchasePriceFooterCell = createFooterCell(
  ArticlePurchasePriceCell,
  0
);
